import { useEffect, useMemo, useRef, useState } from "react";
import { useCampaignId } from "@/features/campaigns/hooks/useCampaignId";
import { useEnrollmentBatchToastStore } from "@/features/campaigns/stores/enrollmentBatchToastStore";
import {
  getSequenceContentProgressSnapshot,
  shouldShowSequenceGeneratingLoader,
} from "@/features/campaigns/utils/enrollmentContentProgress";

/**
 * Unified loader view for Individual Sequence content area.
 * @returns {{ view: 'normal'|'generating'|'sequence', progressSnapshot: object, isGeneratingActive: boolean, completeGeneratingSurface: Function }}
 */
export function useSequenceContentLoaderState({
  sequenceContent,
  isLoading,
  isFetching = false,
  isUpdating,
  prospectId,
}) {
  const campaignId = useCampaignId();
  const batchState = useEnrollmentBatchToastStore();
  const [showGeneratingSurface, setShowGeneratingSurface] = useState(false);
  const wasGeneratingRef = useRef(false);
  const prevProspectIdRef = useRef(prospectId);

  const isGeneratingActive = shouldShowSequenceGeneratingLoader({
    campaignId,
    batchState,
    sequenceData: sequenceContent,
    isLoading,
    isFetching,
  });

  // Only real generation (per the fetched data or an active batch) shows the
  // generating animation. A plain fetch used to count as "generating" because
  // there was no data yet, which then held the animation after already-created
  // content arrived.
  const showGeneratingNow = isGeneratingActive;

  const progressSnapshot = useMemo(
    () =>
      getSequenceContentProgressSnapshot({
        batchState,
        campaignId,
        sequenceData: sequenceContent,
      }),
    [batchState, campaignId, sequenceContent],
  );

  useEffect(() => {
    if (prevProspectIdRef.current !== prospectId) {
      prevProspectIdRef.current = prospectId;
      wasGeneratingRef.current = false;
      setShowGeneratingSurface(showGeneratingNow);
      return;
    }

    if (showGeneratingNow) {
      wasGeneratingRef.current = true;
      setShowGeneratingSurface(true);
      return;
    }

    if (wasGeneratingRef.current) {
      wasGeneratingRef.current = false;
      setShowGeneratingSurface(true);
      return;
    }

    setShowGeneratingSurface(false);
  }, [prospectId, showGeneratingNow]);

  const view = useMemo(() => {
    if (showGeneratingSurface) return "generating";
    if (isUpdating || (isLoading && !sequenceContent)) return "normal";
    return "sequence";
  }, [isLoading, isUpdating, sequenceContent, showGeneratingSurface]);

  const completeGeneratingSurface = () => {
    setShowGeneratingSurface(false);
  };

  return {
    view,
    progressSnapshot,
    isGeneratingActive: showGeneratingNow,
    completeGeneratingSurface,
  };
}
